/**
 * 양식 템플릿 HWP 바이트 (API cm_app_file / IndexedDB 폴백)
 */
import { isBdApiEnabled, FILE_BUCKET } from 'services/pocRowPersist';
import { uploadAppFile, downloadAppFile, deleteAppFile } from 'services/appFileApi';

const DB_NAME = 'erp.cm.form.template';
const DB_VERSION = 1;
const STORE = 'templates';

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB를 사용할 수 없습니다.'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error || new Error('IndexedDB open failed'));
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'formId' });
      }
    };
  });
}

function idbReq(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('IndexedDB request failed'));
  });
}

function toBytes(hwpBytes) {
  if (!hwpBytes) return null;
  if (hwpBytes instanceof ArrayBuffer) return new Uint8Array(hwpBytes);
  if (hwpBytes instanceof Uint8Array) return hwpBytes;
  return new Uint8Array(hwpBytes);
}

export async function saveFormTemplateHwp(formId, hwpBytes, fileName = 'template.hwp') {
  const id = String(formId || '').trim();
  if (!id) throw new Error('formId가 필요합니다.');
  const bytes = toBytes(hwpBytes);
  if (!bytes?.length) throw new Error('HWP 바이트가 없습니다.');
  if (isBdApiEnabled()) {
    await uploadAppFile({
      bucket: FILE_BUCKET.FORM_TEMPLATE,
      objectKey: id,
      file: new Blob([bytes], { type: 'application/octet-stream' }),
      fileName,
    });
    return;
  }
  const db = await openDb();
  try {
    const tx = db.transaction(STORE, 'readwrite');
    await idbReq(
      tx.objectStore(STORE).put({
        formId: id,
        fileName,
        hwpBytes: bytes,
        updatedAt: new Date().toISOString(),
      }),
    );
  } finally {
    db.close();
  }
}

export async function getFormTemplateHwp(formId) {
  const id = String(formId || '').trim();
  if (!id) return null;
  if (isBdApiEnabled()) {
    const blob = await downloadAppFile(FILE_BUCKET.FORM_TEMPLATE, id);
    if (!blob) return null;
    const hwpBytes = new Uint8Array(await blob.arrayBuffer());
    return { formId: id, fileName: 'template.hwp', hwpBytes, updatedAt: '' };
  }
  const db = await openDb();
  try {
    const tx = db.transaction(STORE, 'readonly');
    return (await idbReq(tx.objectStore(STORE).get(id))) || null;
  } finally {
    db.close();
  }
}

export async function deleteFormTemplateHwp(formId) {
  const id = String(formId || '').trim();
  if (!id) return;
  if (isBdApiEnabled()) {
    await deleteAppFile(FILE_BUCKET.FORM_TEMPLATE, id);
    return;
  }
  const db = await openDb();
  try {
    const tx = db.transaction(STORE, 'readwrite');
    await idbReq(tx.objectStore(STORE).delete(id));
  } finally {
    db.close();
  }
}

/**
 * 업로드분(IDB) 우선, 없으면 publicUrl fetch
 * @returns {Promise<{ bytes: Uint8Array, fileName: string, source: 'idb'|'url' }>}
 */
export async function loadFormTemplateBytes(form) {
  if (!form?.formId) throw new Error('양식(formId)이 없습니다.');
  const rec = await getFormTemplateHwp(form.formId);
  if (rec?.hwpBytes?.length) {
    return {
      bytes: toBytes(rec.hwpBytes),
      fileName: rec.fileName || form.templateFile || `${form.formId}.hwp`,
      source: 'idb',
    };
  }
  if (form.publicUrl) {
    const resp = await fetch(form.publicUrl);
    if (!resp.ok) throw new Error(`양식 로드 실패 HTTP ${resp.status}`);
    return {
      bytes: new Uint8Array(await resp.arrayBuffer()),
      fileName: form.templateFile || `${form.formId}.hwp`,
      source: 'url',
    };
  }
  throw new Error('양식 파일이 없습니다. 레지스트리에서 HWP를 업로드하세요.');
}
